import { useState } from "react";
import { Loader2, QrCode, CreditCard, Clock } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PLANS, brl, http, apiError } from "@/lib/api";

const field = "border-white/20 bg-white/5 text-[#F5F2EB] placeholder:text-white/40 focus-visible:ring-1 focus-visible:ring-[var(--gold)]";

const maskPhone = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

export const OrderForm = ({ plan, setPlan, onCreated }) => {
  const [form, setForm] = useState({ name: "", whatsapp: "", email: "", questions: "" });
  const [loading, setLoading] = useState(false);
  const [method, setMethod] = useState("pix");
  const set = (k) => (e) => setForm({ ...form, [k]: k === "whatsapp" ? maskPhone(e.target.value) : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await http.post("/orders", { ...form, plan, email: form.email || null, method, origin_url: window.location.origin });
      localStorage.setItem("maos_order", data?.id ?? "");
      if (data?.checkout_url) { window.location.href = data.checkout_url; return; }
      onCreated(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="order-form">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {PLANS.map((p) => (
          <button type="button" key={p.id} data-testid={`form-plan-${p.id}`} onClick={() => setPlan(p.id)}
            className={`rounded-xl border px-3 py-3 text-left transition-colors duration-300 ${plan === p.id ? "border-[var(--gold)] bg-[#CBA365]/10" : "border-white/15 hover:border-white/35"}`}>
            <span className="block text-xs text-white/70">{p.label}</span>
            <span className="font-display block text-xl text-[var(--gold)]">{brl(p.price)}</span>
          </button>
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="name" className="text-white/80">Nome</Label>
          <Input id="name" required minLength={2} value={form.name} onChange={set("name")} placeholder="Seu nome" className={field} data-testid="order-name-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="wa" className="text-white/80">WhatsApp</Label>
          <Input id="wa" required inputMode="tel" value={form.whatsapp} onChange={set("whatsapp")} placeholder="(41) 99999-9999" className={field} data-testid="order-whatsapp-input" />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="email" className="text-white/80">E-mail <span className="text-white/50">(opcional, para o comprovante)</span></Label>
        <Input id="email" type="email" value={form.email} onChange={set("email")} placeholder="voce@email.com" className={field} data-testid="order-email-input" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="q" className="text-white/80">{plan === "meia-hora" ? "Sobre o que você quer conversar?" : "Suas perguntas"}</Label>
        <Textarea id="q" required minLength={3} rows={4} value={form.questions} onChange={set("questions")}
          placeholder="Escreva aqui, uma pergunta por linha…" className={field} data-testid="order-questions-input" />
      </div>
      <div className="space-y-2">
        <Label className="text-white/80">Forma de pagamento</Label>
        <div className="grid grid-cols-2 gap-2">
          {[["pix", "Pix", QrCode], ["card", "Cartão", CreditCard]].map(([id, label, Icon]) => (
            <button type="button" key={id} data-testid={`pay-method-${id}`} onClick={() => setMethod(id)}
              className={`flex items-center justify-center gap-2 rounded-xl border py-3 text-sm transition-colors duration-300 ${method === id ? "border-[#CBA365] bg-[#CBA365]/10 text-[#CBA365]" : "border-white/15 text-white/75 hover:border-white/35"}`}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>
      </div>
      <div data-testid="order-sla-note" className="flex items-start gap-3 rounded-xl border border-[#CBA365]/30 bg-[#CBA365]/5 p-4">
        <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[#CBA365]" />
        <p className="text-sm text-white/80">Após o pagamento, o atendimento pode ocorrer em até 24 horas, dependendo da quantidade de atendimentos realizados.</p>
      </div>
      <button type="submit" disabled={loading} data-testid="order-submit-btn"
        className="flex w-full items-center justify-center gap-2 rounded-full bg-[var(--gold)] py-4 text-sm font-semibold uppercase tracking-[0.18em] text-[#080312] transition-colors duration-300 hover:bg-[var(--gold-hi)] disabled:opacity-60">
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {method === "pix" ? "Gerar Pix" : "Pagar com cartão"}
      </button>
      <p className="text-center text-xs text-white/55">Pagamento processado com segurança pelo {method === "pix" ? "Mercado Pago" : "Stripe"}.</p>
    </form>
  );
};
